import {computed, DestroyRef, effect, inject, Injectable, Injector, signal, untracked} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {of} from 'rxjs';
import {AppLogger, AppLoggerService} from '../../core/logging';
import {ContextService} from '../context/context-signal.service';
import {UiToastService} from '../../shared/ui';
import {DialogService} from '../dialog/dialog.service';
import {RecipeSuggestionItem} from '../dialog/dialog.models';
import {MEAL_PLAN_GATEWAY} from './meal-plan-gateway';
import {EntryPatch, MealPlanEntry, RecipeSearchResult, Slot} from './meal-plan.models';
import {MealPlanSlot} from './meal-plan-slot.model';
import {MealPlanSlotsService} from './meal-plan-slots.service';
import {addDaysKey, enumerateRange, todayKey} from '../../utils/date-key.util';

/** Days shown at once — a rolling window anchored on today (today + 6). */
const WINDOW_DAYS = 7;

function newClientId(): string {
  return crypto.randomUUID();
}

/**
 * Owns the meal planner's client state: the visible window, the loaded entries,
 * the bucketed grid view model, and optimistic writes.
 *
 * State is signals + `computed` throughout, matching `DialogService` /
 * `ShoppingSyncService`. The window is a rolling `[today, today+6]` range that
 * the user shifts a week at a time; the range is the only thing the gateway is
 * asked for (one request per view).
 */
@Injectable({providedIn: 'root'})
export class MealPlanStore {
  private readonly gateway = inject(MEAL_PLAN_GATEWAY);
  private readonly slotsService = inject(MealPlanSlotsService);
  private readonly context = inject(ContextService);
  private readonly toast = inject(UiToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService = inject(DialogService);
  private readonly injector = inject(Injector);
  private readonly log: AppLogger = inject(AppLoggerService).createForClass(MealPlanStore);

  /** Whole weeks the window is shifted from today. 0 = current window. */
  private readonly _weekOffset = signal(0);
  readonly weekOffset = this._weekOffset.asReadonly();

  /** Inclusive window bounds as date keys. */
  readonly from = computed(() => addDaysKey(todayKey(), this._weekOffset() * WINDOW_DAYS));
  readonly to = computed(() => addDaysKey(this.from(), WINDOW_DAYS - 1));

  /** Ordered date keys in the window — the grid's columns / the list's days. */
  readonly dayKeys = computed(() => enumerateRange(this.from(), this.to()));

  private readonly _entries = signal<MealPlanEntry[]>([]);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  readonly todayKey = todayKey();

  /**
   * Bucketed view model: `date → slot → entries`. Built by one pass over the
   * flat entry list; the empty-cell skeleton is a client concern (iterate
   * `days × slots` and look up this map).
   */
  readonly grid = computed<Map<string, Map<Slot, MealPlanEntry[]>>>(() => {
    const byDate = new Map<string, Map<Slot, MealPlanEntry[]>>();
    for (const key of this.dayKeys()) {
      byDate.set(key, new Map<Slot, MealPlanEntry[]>());
    }
    for (const e of this._entries()) {
      const day = byDate.get(e.planDate);
      if (!day) continue; // outside the window; ignore
      const cell = day.get(e.slot) ?? [];
      cell.push(e);
      day.set(e.slot, cell);
    }
    // Keep each cell in sort order.
    for (const day of byDate.values()) {
      for (const cell of day.values()) {
        cell.sort((a, b) => a.sortOrder - b.sortOrder);
      }
    }
    return byDate;
  });

  /**
   * The household's meal pool: unplaced candidate meals, ordered by sort order.
   * Independent of the visible window — a pool item has no date, so it survives
   * week navigation and page reloads (loaded per household from the backend).
   */
  private readonly _pool = signal<MealPlanEntry[]>([]);
  readonly pool = computed(() => [...this._pool()].sort((a, b) => a.sortOrder - b.sortOrder));

  /** True while an assistant meal-generation request is in flight. */
  private readonly _generating = signal(false);
  readonly generating = this._generating.asReadonly();

  /** The household's server-owned slot catalogue (ordered) — the grid's rows. */
  private readonly _slots = signal<MealPlanSlot[]>([]);
  readonly slots = this._slots.asReadonly();

  private readonly slotByKey = computed(() => new Map(this._slots().map((s) => [s.key, s])));

  /** Slot keys that have at least one entry anywhere in the window. */
  readonly slotKeysWithEntries = computed<Set<Slot>>(() => new Set(this._entries().map((e) => e.slot)));

  /** Localized label for a slot key, falling back to the key if unknown. */
  slotLabel(key: Slot): string {
    return this.slotByKey().get(key)?.label ?? key;
  }

  constructor() {
    // Load the slot catalogue once household context is available (and on switch).
    effect(() => {
      const householdId = this.context.context()?.household?.id;
      if (!householdId) return;
      untracked(() => this.loadSlots(householdId));
    });

    // Load entries on household availability and on every window change. `load`
    // runs in `untracked` so reading `from`/`to`/household here doesn't make the
    // fetch's own signal writes re-trigger this effect (same guard the dialog
    // service uses).
    effect(() => {
      const householdId = this.context.context()?.household?.id;
      const from = this.from();
      const to = this.to();
      if (!householdId) return;
      untracked(() => this.load(householdId, from, to));
    });

    // The pool is per-household, not per-window — load it once on household
    // availability (and on switch), not on every week navigation.
    effect(() => {
      const householdId = this.context.context()?.household?.id;
      if (!householdId) return;
      untracked(() => this.loadPool(householdId));
    });
  }

  private loadPool(householdId: number): void {
    this.gateway
      .getPool(householdId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (items) => this._pool.set(items),
        error: (err) => this.log.error('Failed to load meal pool', err),
      });
  }

  private loadSlots(householdId: number): void {
    this.slotsService
      .getSlots(householdId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (slots) => this._slots.set(slots),
        error: (err) => this.log.error('Failed to load slot catalogue', err),
      });
  }

  private get householdId(): number | null {
    return this.context.context()?.household?.id ?? null;
  }

  // --- Window navigation ---

  nextWeek(): void {
    this._weekOffset.update((o) => o + 1);
  }

  previousWeek(): void {
    this._weekOffset.update((o) => o - 1);
  }

  resetToToday(): void {
    this._weekOffset.set(0);
  }

  reload(): void {
    const hid = this.householdId;
    if (hid) {
      this.load(hid, this.from(), this.to());
      this.loadPool(hid);
    }
  }

  private load(householdId: number, from: string, to: string): void {
    this._loading.set(true);
    this._error.set(null);
    this.gateway
      .getRange(householdId, from, to)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (days) => {
          this._entries.set(days.flatMap((d) => d.entries));
          this._loading.set(false);
        },
        error: (err) => {
          this.log.error('Failed to load meal plan', err);
          this._error.set('Menüplan konnte nicht geladen werden.');
          this._loading.set(false);
        },
      });
  }

  // --- Optimistic writes ---

  /** Create a recipe entry: render immediately, POST, reconcile by clientId. */
  addRecipe(planDate: string, slot: Slot, recipe: RecipeSearchResult): void {
    this.addEntry(planDate, slot, {
      title: recipe.title,
      servings: recipe.servings,
      recipe: {
        id: recipe.id,
        servings: recipe.servings,
        prepTimeMinutes: recipe.prepTimeMinutes,
        totalTimeMinutes: recipe.totalTimeMinutes,
      },
      draftRecipeId: recipe.id,
    });
  }

  /** Create a free-text entry ("Eat out", "Leftovers", …). */
  addFreeText(planDate: string, slot: Slot, title: string): void {
    this.addEntry(planDate, slot, {title, servings: null, recipe: null, draftRecipeId: null});
  }

  /** Add a recipe candidate to the pool (unplaced). Drag it into a cell to plan it. */
  addRecipeToPool(recipe: RecipeSearchResult): void {
    this.addPoolItem({
      title: recipe.title,
      servings: recipe.servings,
      recipe: {
        id: recipe.id,
        servings: recipe.servings,
        prepTimeMinutes: recipe.prepTimeMinutes,
        totalTimeMinutes: recipe.totalTimeMinutes,
      },
      draftRecipeId: recipe.id,
    });
  }

  /** Add a free-text candidate to the pool (a rough idea, no recipe yet). */
  addFreeTextToPool(title: string): void {
    this.addPoolItem({title, servings: null, recipe: null, draftRecipeId: null});
  }

  /**
   * Generate assistant meal ideas into the pool — the backend builds them from
   * stock (expiring first), food preferences, and the household's saved recipes.
   * Appends the created (server-owned) entries; the `assistant` source lets the
   * UI badge them.
   */
  generatePool(count?: number, theme?: string): void {
    const hid = this.householdId;
    if (!hid || this._generating()) return;
    this._generating.set(true);

    this.gateway
      .generatePool(hid, count, theme)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (items) => {
          this._generating.set(false);
          if (items.length > 0) {
            this._pool.update((p) => [...p, ...items]);
          }
        },
        error: (err) => {
          this._generating.set(false);
          this.log.error('Failed to generate meal ideas', err);
          this.toast.error('Vorschläge konnten nicht erstellt werden.');
        },
      });
  }

  /**
   * Turn a free-text idea (title + optional note, no recipe yet — e.g. one
   * `generatePool` produced) into a real, saved recipe. Works for an idea
   * sitting in the pool *or* already placed on a day. Opens the assistant on
   * the existing brainstorm→commit path (`authorSuggestion`, the same one the
   * chat's own idea cards use) so the strong model authors a full recipe from
   * the idea; once the user saves the resulting draft, the *same* entry (pool
   * card or placed chip) is updated to reference the new recipe instead of
   * leaving a stale duplicate title-only entry next to it.
   */
  convertEntryToRecipe(entry: MealPlanEntry): void {
    if (entry.id == null || entry.recipe != null) return;
    const entryId = entry.id;

    const idea: RecipeSuggestionItem = {title: entry.title, description: entry.note};
    this.dialogService.open();
    this.dialogService
      .authorSuggestion(idea)
      .then((messageId) => {
        if (messageId) this.watchForAuthoredRecipe(messageId, entryId);
      })
      .catch((err) => this.log.error('Failed to start recipe authoring from idea', err));
  }

  /**
   * Watches the chat message `authorSuggestion` created until its `recipeJob`
   * reaches a terminal status, then attaches the created recipe to the
   * originating entry (or just stops watching on failure — the chat already
   * shows the failure, nothing more to do here). Self-cleaning: the effect
   * destroys itself as soon as it has an answer, so nothing keeps watching past
   * that single turn.
   */
  private watchForAuthoredRecipe(messageId: string, entryId: number): void {
    const watcher = effect(
      () => {
        const job = this.dialogService.messages().find((m) => m.id === messageId)?.recipeJob;
        if (job?.status === 'succeeded' && job.createdRecipeId != null) {
          watcher.destroy();
          this.attachRecipeToEntry(entryId, job.createdRecipeId);
        } else if (job?.status === 'failed') {
          watcher.destroy();
        }
      },
      {injector: this.injector, manualCleanup: true},
    );
    this.destroyRef.onDestroy(() => watcher.destroy());
  }

  /** Replaces the entry (wherever it lives — pool or placed) with the
   *  server's recipe-backed version, then reloads the plan + pool so the whole
   *  view is consistent with the server (the entry's title/note were cleared
   *  server-side, and a reload avoids any stale local copy lingering). */
  private attachRecipeToEntry(entryId: number, recipeId: number): void {
    this.gateway
      .updateEntry(entryId, {recipeId})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          this._pool.update((p) => p.map((e) => (e.id === entryId ? saved : e)));
          this._entries.update((es) => es.map((e) => (e.id === entryId ? saved : e)));
          this.reload();
        },
        error: (err) => {
          this.log.error('Failed to attach authored recipe to entry', err, {entryId, recipeId});
          this.toast.error('Rezept konnte nicht mit der Vorschlagskarte verknüpft werden.');
        },
      });
  }

  private addPoolItem(opts: {
    title: string;
    servings: number | null;
    recipe: MealPlanEntry['recipe'];
    draftRecipeId: number | null;
  }): void {
    const hid = this.householdId;
    if (!hid) return;
    const clientId = newClientId();
    const optimistic: MealPlanEntry = {
      id: null,
      clientId,
      planDate: '', // pooled = unplaced
      slot: '',
      sortOrder: this.nextPoolSortOrder(),
      title: opts.title,
      servings: opts.servings,
      portionOfEntryId: null,
      recipe: opts.recipe,
      source: 'manual',
      pending: true,
    };
    this._pool.update((p) => [...p, optimistic]);

    this.gateway
      .createPoolItem(hid, {
        clientId,
        recipeId: opts.draftRecipeId,
        title: opts.recipe ? undefined : opts.title,
        servings: opts.servings,
        source: 'manual',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) =>
          this._pool.update((p) =>
            p.map((e) => (e.clientId === clientId ? {...saved, pending: false, failed: false} : e)),
          ),
        error: (err) => {
          this.log.error('Failed to create pool item', err);
          this._pool.update((p) =>
            p.map((e) => (e.clientId === clientId ? {...e, pending: false, failed: true} : e)),
          );
          this.toast.error('Vorschlag konnte nicht gespeichert werden.');
        },
      });
  }

  private addEntry(
    planDate: string,
    slot: Slot,
    opts: {
      title: string;
      servings: number | null;
      recipe: MealPlanEntry['recipe'];
      draftRecipeId: number | null;
    },
  ): void {
    const hid = this.householdId;
    if (!hid) return;
    const clientId = newClientId();
    const optimistic: MealPlanEntry = {
      id: null,
      clientId,
      planDate,
      slot,
      sortOrder: this.nextSortOrder(planDate, slot),
      title: opts.title,
      servings: opts.servings,
      portionOfEntryId: null,
      recipe: opts.recipe,
      pending: true,
    };
    this._entries.update((es) => [...es, optimistic]);

    this.gateway
      .createEntry(hid, {
        clientId,
        planDate,
        slot,
        recipeId: opts.draftRecipeId,
        title: opts.recipe ? undefined : opts.title,
        servings: opts.servings,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => this.reconcile(clientId, saved),
        error: (err) => {
          this.log.error('Failed to create entry', err);
          this._entries.update((es) =>
            es.map((e) => (e.clientId === clientId ? {...e, pending: false, failed: true} : e)),
          );
          this.toast.error('Eintrag konnte nicht gespeichert werden.');
        },
      });
  }

  private reconcile(clientId: string, saved: MealPlanEntry): void {
    this._entries.update((es) =>
      es.map((e) => (e.clientId === clientId ? {...saved, pending: false, failed: false} : e)),
    );
  }

  /** Drop a failed/pending optimistic card from wherever it lives (grid or pool). */
  removeFailed(clientId: string): void {
    this._entries.update((es) => es.filter((e) => e.clientId !== clientId));
    this._pool.update((p) => p.filter((e) => e.clientId !== clientId));
  }

  /**
   * Retry a failed create in place — re-issues it into the same location. A pool
   * item (empty `planDate`) re-pools; a placed entry re-creates in its cell.
   */
  retry(entry: MealPlanEntry): void {
    if (!entry.clientId) return;
    this.removeFailed(entry.clientId);
    const recipe = entry.recipe;
    if (!entry.planDate) {
      // Pool item.
      if (recipe) {
        this.addRecipeToPool({
          id: recipe.id,
          title: entry.title,
          servings: recipe.servings,
          prepTimeMinutes: recipe.prepTimeMinutes,
          totalTimeMinutes: recipe.totalTimeMinutes,
        });
      } else {
        this.addFreeTextToPool(entry.title);
      }
      return;
    }
    if (recipe) {
      this.addRecipe(entry.planDate, entry.slot, {
        id: recipe.id,
        title: entry.title,
        servings: recipe.servings,
        prepTimeMinutes: recipe.prepTimeMinutes,
        totalTimeMinutes: recipe.totalTimeMinutes,
      });
    } else {
      this.addFreeText(entry.planDate, entry.slot, entry.title);
    }
  }

  /** Delete an entry (grid or pool): optimistic remove, restore + toast on failure. */
  deleteEntry(entry: MealPlanEntry): void {
    if (entry.id == null) {
      // Never persisted (still pending/failed) — just drop it locally.
      if (entry.clientId) this.removeFailed(entry.clientId);
      return;
    }
    const sig = this.signalFor(entry);
    const snapshot = sig();
    sig.update((es) => es.filter((e) => e.id !== entry.id));
    this.gateway
      .deleteEntry(entry.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: (err) => {
          this.log.error('Failed to delete entry', err);
          sig.set(snapshot);
          this.toast.error('Eintrag konnte nicht gelöscht werden.');
        },
      });
  }

  /** Edit servings/note (grid or pool): optimistic patch, restore + toast on failure. */
  updateEntry(entry: MealPlanEntry, patch: EntryPatch): void {
    if (entry.id == null) return;
    const sig = this.signalFor(entry);
    const snapshot = sig();
    sig.update((es) => es.map((e) => (e.id === entry.id ? {...e, ...patch} : e)));
    this.gateway
      .updateEntry(entry.id, patch)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        // Preserve the local day/slot: a servings/note PATCH response may not
        // carry plan_date, and this edit never changes placement anyway.
        next: (saved) =>
          sig.update((es) =>
            es.map((e) => (e.id === saved.id ? {...e, ...saved, planDate: e.planDate, slot: e.slot} : e)),
          ),
        error: (err) => {
          this.log.error('Failed to update entry', err);
          sig.set(snapshot);
          this.toast.error('Eintrag konnte nicht aktualisiert werden.');
        },
      });
  }

  /**
   * Move an entry to a different day/slot: optimistic move, restore + toast on
   * failure. Only persisted entries can move — a pending/failed create isn't on
   * the server yet, so there is nothing to PATCH.
   */
  moveEntry(entry: MealPlanEntry, targetDate: string, targetSlot: Slot): void {
    if (entry.id == null) return;
    if (entry.planDate === targetDate && entry.slot === targetSlot) return; // no-op

    const snapshot = this._entries();
    const sortOrder = this.nextSortOrder(targetDate, targetSlot);
    this._entries.update((es) =>
      es.map((e) => (e.id === entry.id ? {...e, planDate: targetDate, slot: targetSlot, sortOrder} : e)),
    );

    this.gateway
      .updateEntry(entry.id, {planDate: targetDate, slot: targetSlot})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        // Keep the target placement explicitly: a PATCH echo can't carry the
        // enclosing day, so the gateway maps planDate from the request.
        next: (saved) =>
          this._entries.update((es) =>
            es.map((e) => (e.id === saved.id ? {...saved, planDate: targetDate, slot: targetSlot} : e)),
          ),
        error: (err) => {
          this.log.error('Failed to move entry', err);
          this._entries.set(snapshot);
          this.toast.error('Eintrag konnte nicht verschoben werden.');
        },
      });
  }

  /**
   * Place a pool item into a day/slot cell — the pool→grid drag. Optimistically
   * moves the card out of the pool and into the grid, then persists the placement
   * as a move (PATCH plan_date/slot); restores both lists on failure.
   */
  placeFromPool(entry: MealPlanEntry, targetDate: string, targetSlot: Slot): void {
    if (entry.id == null) return; // still pending its create — nothing to move yet
    const poolSnapshot = this._pool();
    const entriesSnapshot = this._entries();
    const sortOrder = this.nextSortOrder(targetDate, targetSlot);

    this._pool.update((p) => p.filter((e) => e.id !== entry.id));
    this._entries.update((es) => [...es, {...entry, planDate: targetDate, slot: targetSlot, sortOrder}]);

    this.gateway
      .updateEntry(entry.id, {planDate: targetDate, slot: targetSlot})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) =>
          this._entries.update((es) =>
            es.map((e) => (e.id === saved.id ? {...saved, planDate: targetDate, slot: targetSlot} : e)),
          ),
        error: (err) => {
          this.log.error('Failed to place pool item', err);
          this._pool.set(poolSnapshot);
          this._entries.set(entriesSnapshot);
          this.toast.error('Mahlzeit konnte nicht eingeplant werden.');
        },
      });
  }

  /**
   * Return a placed entry to the pool — the grid→pool drag. Optimistically moves
   * the card out of the grid and into the pool, then persists via the dedicated
   * unplace verb; restores both lists on failure.
   */
  unplaceToPool(entry: MealPlanEntry): void {
    if (entry.id == null) return;
    if (!entry.planDate && !entry.slot) return; // already pooled — no-op
    const poolSnapshot = this._pool();
    const entriesSnapshot = this._entries();

    this._entries.update((es) => es.filter((e) => e.id !== entry.id));
    this._pool.update((p) => [...p, {...entry, planDate: '', slot: '', sortOrder: this.nextPoolSortOrder()}]);

    this.gateway
      .unplaceEntry(entry.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => this._pool.update((p) => p.map((e) => (e.id === saved.id ? saved : e))),
        error: (err) => {
          this.log.error('Failed to unplace entry', err);
          this._pool.set(poolSnapshot);
          this._entries.set(entriesSnapshot);
          this.toast.error('Mahlzeit konnte nicht in den Pool verschoben werden.');
        },
      });
  }

  searchRecipes(query: string) {
    const hid = this.householdId;
    if (!hid) return of<RecipeSearchResult[]>([]);
    return this.gateway.searchRecipes(hid, query);
  }

  private nextSortOrder(planDate: string, slot: Slot): number {
    const inCell = this._entries().filter((e) => e.planDate === planDate && e.slot === slot);
    const max = inCell.reduce((m, e) => Math.max(m, e.sortOrder), 0);
    return max + 100;
  }

  private nextPoolSortOrder(): number {
    const max = this._pool().reduce((m, e) => Math.max(m, e.sortOrder), 0);
    return max + 100;
  }

  /** Which backing list an entry lives in — the pool (unplaced) or the grid. */
  private signalFor(entry: MealPlanEntry) {
    const inPool = this._pool().some(
      (e) => (entry.id != null && e.id === entry.id) || (entry.clientId != null && e.clientId === entry.clientId),
    );
    return inPool ? this._pool : this._entries;
  }
}
